import { useState, useEffect, useCallback } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button, Input, Textarea } from '@/components/ui';
import { PrayerRequest, PrayerType, PrayerStatus, UpdatePrayerRequest } from '@/types';
import { api } from '@/lib/api';
import { cn, formatDate } from '@/lib/utils';
import { formatFullName } from '@/lib/format';
import { stripEmojisAndOddCharacters } from '@/lib/validation';
import {
  CATEGORY_OPTIONS,
  categoriesEqual,
  getPrayerCategories,
  normalizeCategories,
} from '@/lib/categories';

export interface PrayerEditorProps {
  prayer: PrayerRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, updated: UpdatePrayerRequest) => Promise<void>;
  isSaving?: boolean;
}

/** Prior submission from the same submitter (GET related-submissions). */
interface RelatedSubmission {
  id: string;
  createdAt: string;
  type: PrayerType;
  status: PrayerStatus;
  body: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  location: string | null;
  isAnonymous: boolean;
  submittedVia: string;
}

interface RelatedSubmissionsResponse {
  prayerId: string;
  total: number;
  submissions: RelatedSubmission[];
}

/** Content state before the first recorded content edit. */
interface OriginalPrayerSnapshot {
  body: string;
  firstName: string | null;
  lastName: string | null;
  location: string | null;
  type: PrayerType | string;
  categories: string[];
}

interface PrayerEditEntry {
  id: string;
  userId: string | null;
  userName: string;
  userEmail: string | null;
  changedFields: string[];
  createdAt: string;
}

interface PrayerEditHistoryResponse {
  prayerId: string;
  original: OriginalPrayerSnapshot | null;
  edits: PrayerEditEntry[];
}

type EditorView = 'edit' | 'history' | 'editHistory';

const statusOptions = [
  { value: PrayerStatus.PENDING, label: 'Pending' },
  { value: PrayerStatus.QUEUED, label: 'Queued' },
  { value: PrayerStatus.DISPLAYED, label: 'Displayed' },
  { value: PrayerStatus.ARCHIVED, label: 'Dismissed' },
];

const statusLabels: Record<string, string> = {
  [PrayerStatus.PENDING]: 'Pending',
  [PrayerStatus.QUEUED]: 'Queued',
  [PrayerStatus.DISPLAYED]: 'Displayed',
  [PrayerStatus.ARCHIVED]: 'Dismissed',
};

const CHANGED_FIELD_LABELS: Record<string, string> = {
  body: 'Prayer text',
  first_name: 'Name',
  last_name: 'Name',
  location: 'Location',
  type: 'Type',
  categories: 'Categories',
};

function typeLabel(type: PrayerType | string): string {
  if (type === PrayerType.PRAISE || type === PrayerType.PRAISE_REPORT) {
    return 'Answered Prayer';
  }
  return 'Prayer Request';
}

function categoryLabels(categories: string[]): string {
  if (!categories.length) return 'None';
  return categories
    .map((c) => CATEGORY_OPTIONS.find((o) => o.value === c)?.label ?? c)
    .join(', ');
}

/** Deduplicate Name when both first_name and last_name changed. */
function formatChangedFields(fields: string[]): string {
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const field of fields) {
    const label = CHANGED_FIELD_LABELS[field] ?? field;
    if (!seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels.join(', ');
}

export function PrayerEditor({
  prayer,
  isOpen,
  onClose,
  onSave,
  isSaving = false,
}: PrayerEditorProps) {
  const [body, setBody] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [location, setLocation] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [type, setType] = useState<PrayerType>(PrayerType.PRAYER_REQUEST);
  const [status, setStatus] = useState<PrayerStatus>(PrayerStatus.PENDING);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [view, setView] = useState<EditorView>('edit');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyItems, setHistoryItems] = useState<RelatedSubmission[]>([]);

  const [editHistoryLoading, setEditHistoryLoading] = useState(false);
  const [editHistoryError, setEditHistoryError] = useState<string | null>(null);
  const [editHistoryOriginal, setEditHistoryOriginal] = useState<OriginalPrayerSnapshot | null>(
    null
  );
  const [editHistoryEdits, setEditHistoryEdits] = useState<PrayerEditEntry[]>([]);

  const normalizePrayerTypeForUI = (t: PrayerType): PrayerType => {
    // Backend may send `prayer_request` / `praise_report`.
    // This editor UI expects `prayer` / `praise` so the radio buttons show correctly.
    if (t === PrayerType.PRAYER_REQUEST) return PrayerType.PRAYER;
    if (t === PrayerType.PRAISE_REPORT) return PrayerType.PRAISE;
    return t;
  };

  const toggleCategory = (value: string) => {
    setCategories((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  const resetSecondaryViews = useCallback(() => {
    setView('edit');
    setHistoryLoading(false);
    setHistoryError(null);
    setHistoryTotal(0);
    setHistoryItems([]);
  }, []);

  const resetEditHistoryState = useCallback(() => {
    setEditHistoryLoading(false);
    setEditHistoryError(null);
    setEditHistoryOriginal(null);
    setEditHistoryEdits([]);
  }, []);

  const loadEditHistory = useCallback(async (prayerId: string) => {
    setEditHistoryLoading(true);
    setEditHistoryError(null);
    const res = await api.get<PrayerEditHistoryResponse>(
      `/prayer-requests/${prayerId}/edit-history`
    );
    if (res.error) {
      setEditHistoryError(res.error);
      setEditHistoryOriginal(null);
      setEditHistoryEdits([]);
    } else if (res.data) {
      setEditHistoryOriginal(res.data.original);
      setEditHistoryEdits(res.data.edits);
    }
    setEditHistoryLoading(false);
  }, []);

  // Reset form when prayer changes
  useEffect(() => {
    if (prayer) {
      setBody(stripEmojisAndOddCharacters(prayer.body));
      setFirstName(prayer.firstName ?? '');
      setLastName(prayer.lastName ?? '');
      setLocation(prayer.location ?? '');
      setCategories(getPrayerCategories(prayer));
      setType(normalizePrayerTypeForUI(prayer.type));
      setStatus(prayer.status);
      setErrors({});
      resetSecondaryViews();
      resetEditHistoryState();
    }
  }, [prayer, resetSecondaryViews, resetEditHistoryState]);

  // Load edit history when the modal opens so attribution is ready on the form
  useEffect(() => {
    if (isOpen && prayer?.id) {
      void loadEditHistory(prayer.id);
    }
    if (!isOpen) {
      resetSecondaryViews();
      resetEditHistoryState();
    }
  }, [isOpen, prayer?.id, loadEditHistory, resetSecondaryViews, resetEditHistoryState]);

  const loadHistory = async () => {
    if (!prayer) return;
    setView('history');
    setHistoryLoading(true);
    setHistoryError(null);
    setHistoryItems([]);
    setHistoryTotal(0);
    const res = await api.get<RelatedSubmissionsResponse>(
      `/prayer-requests/${prayer.id}/related-submissions`
    );
    if (res.error) {
      setHistoryError(res.error);
    } else if (res.data) {
      setHistoryTotal(res.data.total);
      setHistoryItems(res.data.submissions);
    }
    setHistoryLoading(false);
  };

  const openEditHistory = async () => {
    if (!prayer) return;
    setView('editHistory');
    await loadEditHistory(prayer.id);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const strippedBody = stripEmojisAndOddCharacters(body);

    if (!strippedBody) {
      newErrors.body = 'Prayer text is required';
    } else if (strippedBody.length > 250) {
      newErrors.body = 'Prayer text must be less than 250 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!prayer || !validate()) return;

    const updates: UpdatePrayerRequest = {};
    const strippedBody = stripEmojisAndOddCharacters(body);
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedLocation = location.trim();
    const nextCategories = normalizeCategories(categories);
    const prevCategories = getPrayerCategories(prayer);

    // Only include changed fields
    if (strippedBody !== prayer.body) {
      updates.body = strippedBody;
    }
    if (trimmedFirst !== (prayer.firstName ?? '') || trimmedLast !== (prayer.lastName ?? '')) {
      updates.firstName = trimmedFirst || undefined;
      updates.lastName = trimmedLast || undefined;
    }
    if (trimmedLocation !== (prayer.location ?? '')) {
      updates.location = trimmedLocation || undefined;
    }
    if (!categoriesEqual(nextCategories, prevCategories)) {
      updates.categories = nextCategories;
    }
    if (type !== prayer.type) {
      // Map frontend UI types to backend enum values
      // Frontend uses PRAYER/PRAISE for UI, backend expects PRAYER_REQUEST/PRAISE_REPORT
      if (type === PrayerType.PRAYER) {
        updates.type = PrayerType.PRAYER_REQUEST;
      } else if (type === PrayerType.PRAISE) {
        updates.type = PrayerType.PRAISE_REPORT;
      } else {
        // Already in backend format (PRAYER_REQUEST/PRAISE_REPORT)
        updates.type = type;
      }
    }
    if (status !== prayer.status) {
      updates.status = status;
    }

    // Only save if there are changes
    if (Object.keys(updates).length > 0) {
      await onSave(prayer.id, updates);
    }

    onClose();
  };

  if (!prayer) return null;

  const showingPastSubmissions = view === 'history';
  const showingEditHistory = view === 'editHistory';
  const showingSecondary = showingPastSubmissions || showingEditHistory;
  const latestEdit = editHistoryEdits[0] ?? null;
  const editCount = editHistoryEdits.length;

  const modalTitle = showingPastSubmissions
    ? 'Past submissions'
    : showingEditHistory
      ? 'Edit history'
      : 'Edit Prayer Request';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      footer={
        showingSecondary ? (
          <Button variant="outline" onClick={() => setView('edit')}>
            Back
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              isLoading={isSaving}
              disabled={isSaving}
            >
              Save Changes
            </Button>
          </>
        )
      }
    >
      {showingPastSubmissions ? (
        <div className="space-y-4">
          {historyLoading && (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
          )}
          {historyError && (
            <p className="text-sm text-red-600 dark:text-red-400">{historyError}</p>
          )}
          {!historyLoading && !historyError && historyItems.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No other submissions from this submitter
            </p>
          )}
          {!historyLoading && !historyError && historyItems.length > 0 && (
            <>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {historyTotal} other submission{historyTotal === 1 ? '' : 's'}
                {historyTotal > historyItems.length
                  ? ` (showing ${historyItems.length} most recent)`
                  : ''}
              </p>
              <ul className="space-y-3">
                {historyItems.map((item) => {
                  const name = item.isAnonymous
                    ? 'Anonymous'
                    : formatFullName(item.firstName, item.lastName) || 'No name';
                  return (
                    <li
                      key={item.id}
                      className="rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                    >
                      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <span>{formatDate(item.createdAt)}</span>
                        <span aria-hidden="true">·</span>
                        <span>{typeLabel(item.type)}</span>
                        <span aria-hidden="true">·</span>
                        <span>{statusLabels[item.status] ?? item.status}</span>
                        {item.isAnonymous && (
                          <>
                            <span aria-hidden="true">·</span>
                            <span className="italic">Anonymous</span>
                          </>
                        )}
                      </div>
                      <p className="mb-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                        {name}
                      </p>
                      <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                        {item.body}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      ) : showingEditHistory ? (
        <div className="space-y-6">
          {editHistoryLoading && (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
          )}
          {editHistoryError && (
            <p className="text-sm text-red-600 dark:text-red-400">{editHistoryError}</p>
          )}
          {!editHistoryLoading && !editHistoryError && (
            <>
              <section className="space-y-3">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Original request
                </h3>
                {editHistoryOriginal ? (
                  <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                    <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                      <span>{typeLabel(editHistoryOriginal.type)}</span>
                      <span aria-hidden="true">·</span>
                      <span>{categoryLabels(editHistoryOriginal.categories ?? [])}</span>
                    </div>
                    <p className="mb-1 text-sm font-medium text-slate-800 dark:text-slate-200">
                      {formatFullName(
                        editHistoryOriginal.firstName,
                        editHistoryOriginal.lastName
                      ) || 'No name'}
                    </p>
                    {editHistoryOriginal.location && (
                      <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                        {editHistoryOriginal.location}
                      </p>
                    )}
                    <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                      {editHistoryOriginal.body}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {editCount > 0
                      ? 'The original request was not saved for edits made before edit history was enabled.'
                      : 'No content edits yet. The original request is the current text.'}
                  </p>
                )}
              </section>

              <section className="space-y-3">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Edits{editCount > 0 ? ` (${editCount})` : ''}
                </h3>
                {editCount === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No content edits recorded
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {editHistoryEdits.map((edit) => (
                      <li
                        key={edit.id}
                        className="rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                      >
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                          {edit.userName}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {formatDate(edit.createdAt)}
                        </p>
                        {edit.changedFields.length > 0 && (
                          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                            Changed: {formatChangedFields(edit.changedFields)}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Submitter Info: Name and Location editable; rest read-only */}
          <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-900/50">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Submitter Information
              </h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openEditHistory}
                  disabled={isSaving}
                >
                  Edit history
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={loadHistory}
                  disabled={isSaving}
                >
                  Past submissions
                </Button>
              </div>
            </div>
            {!editHistoryLoading && latestEdit && (
              <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                Last edited by {latestEdit.userName} on {formatDate(latestEdit.createdAt)}
                {editCount > 1 ? ` · ${editCount} edits` : ''}
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Input
                  label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Not provided"
                  autoComplete="given-name"
                />
              </div>
              <div>
                <Input
                  label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Not provided"
                  autoComplete="family-name"
                />
                {prayer.isAnonymous && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Displayed as Anonymous
                  </p>
                )}
              </div>
              <div>
                <Input
                  label="Location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Not provided"
                  autoComplete="address-level2"
                />
              </div>
            </div>
            <dl className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Email</dt>
                <dd className="text-sm text-slate-900 dark:text-white">
                  {prayer.email || <span className="text-slate-400">Not provided</span>}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Phone</dt>
                <dd className="text-sm text-slate-900 dark:text-white">
                  {prayer.phone || <span className="text-slate-400">Not provided</span>}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Submitted Via</dt>
                <dd className="text-sm text-slate-900 dark:text-white capitalize">
                  {prayer.submittedVia}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500 dark:text-slate-400">Submitted At</dt>
                <dd className="text-sm text-slate-900 dark:text-white">
                  {formatDate(prayer.createdAt)}
                </dd>
              </div>
            </dl>
          </div>

          {/* Editable Fields */}
          <div className="space-y-4">
            {/* Prayer Body */}
            <div>
              <Textarea
                label="Prayer Text"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                error={errors.body}
                placeholder="Enter the prayer request or answered prayer..."
                maxLength={250}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {body.length}/250 characters
              </p>
            </div>

            {/* Type Selection */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Type
              </label>
              <div className="flex gap-4">
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 transition-colors',
                    type === PrayerType.PRAYER
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  )}
                >
                  <input
                    type="radio"
                    name="type"
                    value={PrayerType.PRAYER}
                    checked={type === PrayerType.PRAYER}
                    onChange={() => setType(PrayerType.PRAYER)}
                    className="sr-only"
                  />
                  <svg
                    className={cn(
                      'h-5 w-5',
                      type === PrayerType.PRAYER
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-slate-400'
                    )}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                    />
                  </svg>
                  <span
                    className={cn(
                      'text-sm font-medium',
                      type === PrayerType.PRAYER
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-slate-700 dark:text-slate-300'
                    )}
                  >
                    Prayer Request
                  </span>
                </label>

                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2.5 transition-colors',
                    type === PrayerType.PRAISE
                      ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                  )}
                >
                  <input
                    type="radio"
                    name="type"
                    value={PrayerType.PRAISE}
                    checked={type === PrayerType.PRAISE}
                    onChange={() => setType(PrayerType.PRAISE)}
                    className="sr-only"
                  />
                  <svg
                    className={cn(
                      'h-5 w-5',
                      type === PrayerType.PRAISE
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-slate-400'
                    )}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                    />
                  </svg>
                  <span
                    className={cn(
                      'text-sm font-medium',
                      type === PrayerType.PRAISE
                        ? 'text-amber-700 dark:text-amber-300'
                        : 'text-slate-700 dark:text-slate-300'
                    )}
                  >
                    Answered Prayer
                  </span>
                </label>
              </div>
            </div>

            {/* Categories and Status */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Categories
                </span>
                <div className="flex flex-col gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2.5 dark:border-slate-600 dark:bg-slate-800">
                  {CATEGORY_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-300"
                    >
                      <input
                        type="checkbox"
                        checked={categories.includes(option.value)}
                        onChange={() => toggleCategory(option.value)}
                        className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as PrayerStatus)}
                  className={cn(
                    'block w-full rounded-lg border bg-white px-3 py-2.5 text-sm',
                    'border-slate-300 dark:border-slate-600 dark:bg-slate-800',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                  )}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}
